import { Calendar, EventsList, Text, View } from "@/components";
import { useLayout } from "@/providers";

import type { CalendarEvent } from "@/types";

/**
 * CalendarWidget Component
 *
 * @description
 * A composite widget that combines the Calendar component and the EventsList component.
 * It displays a calendar view alongside a list of events.
 *
 * Use this component in dashboards or pages where a schedule overview is needed.
 *
 * @component
 * @param {object} props - The component props.
 * @param {CalendarEvent[]} props.events - Array of calendar events to display in the list.
 *
 * @returns {JSX.Element} The rendered CalendarWidget.
 *
 * @example
 * ```tsx
 * import { calendarEvents } from "@/app/mocks/calendar";
 *
 * <CalendarWidget events={calendarEvents} />
 * ```
 */
const CalendarWidget = ({ events }: { events: CalendarEvent[] }) => {
  const { t } = useLayout();
  return (
    <View className="flex flex-col gap-6">
      <View className="flex items-center gap-3">
        <View className="h-8 w-1.5 rounded-full bg-primary" />
        <Text type="h2" className="text-2xl font-bold text-foreground">
          {t("calendar")}
        </Text>
      </View>

      <View className="flex flex-col gap-8 xl:flex-row">
        <View className="shrink-0 xl:w-[400px]">
          <Calendar />
        </View>
        <View className="flex-1">
          <EventsList events={events} />
        </View>
      </View>
    </View>
  );
};

export default CalendarWidget;
